import { useEffect, useRef, useState } from 'react'

export const normalizeDash = (s) => s.replace(/['’]/g, '-')

export const formatDocumentSync = (value) => {
  const replaced = normalizeDash(value)

  const compact = replaced.replace(/[.\-\s]/g, '')
  const isRutLike = /^[0-9Kk]+$/.test(compact)
  if (!isRutLike) {
    return replaced.replace(/[^a-zA-Z0-9]/g, '').toUpperCase()
  }

  const rut = compact.replace(/[^0-9K]/gi, '')
  if (rut.length < 2) return ''
  const body = rut.slice(0, -1).replace(/\B(?=(\d{3})+(?!\d))/g, '.')
  const dv = rut.slice(-1).toUpperCase()
  return `${body}-${dv}`
}

export const extractRutFromText = (text) => {
  if (!text) return null
  const raw = normalizeDash(text)
  try {
    const m = raw.match(/[?&]RUN=([^&]+)/i)
    if (m && m[1]) {
      const val = decodeURIComponent(m[1]).trim()
      const candidate = val.replace(/\./g, '')
      const m2 = candidate.match(/^(\d{6,9})-?([0-9Kk])$/)
      if (m2) return `${m2[1]}-${m2[2].toUpperCase()}`
    }
  } catch (_) {}
  const generic = raw.match(/(\d{6,9})[-’']?([0-9Kk])/)
  if (generic) return `${generic[1]}-${generic[2].toUpperCase()}`
  return null
}

/**
 * idleMs: number | null → si se define, procesará por inactividad además de Enter
 * scanSinkRef: Ref a un elemento contentEditable que capta las teclas (PC sin Enter)
 * scanInputRef: Ref a input (iOS/Android con teclado virtual tipo ScanKey)
 */
export const useGunMode = ({
  enabled,
  autoSubmit,
  defaultEvent,
  canAutoSubmitNow,
  onInsert,
  onSubmit,
  toast,
  idleMs = 800,
  scanSinkRef = null,   // NUEVO: contentEditable (PC sin Enter)
  scanInputRef = null,  // existente: input para móviles
}) => {
  const [buffer, setBuffer] = useState('')
  const bufferRef = useRef(buffer)
  const enabledRef = useRef(enabled)
  const autoSubmitRef = useRef(autoSubmit)
  const defaultEventRef = useRef(defaultEvent)
  const canAutoSubmitRef = useRef(canAutoSubmitNow)
  const idleTimerRef = useRef(null)

  const sinkElRef = useRef(null)
  const inputElRef = useRef(null)

  useEffect(() => { bufferRef.current = buffer }, [buffer])
  useEffect(() => { enabledRef.current = enabled }, [enabled])
  useEffect(() => { autoSubmitRef.current = autoSubmit }, [autoSubmit])
  useEffect(() => { defaultEventRef.current = defaultEvent }, [defaultEvent])
  useEffect(() => { canAutoSubmitRef.current = canAutoSubmitNow }, [canAutoSubmitNow])

  useEffect(() => { sinkElRef.current = scanSinkRef?.current || null }, [scanSinkRef])
  useEffect(() => { inputElRef.current = scanInputRef?.current || null }, [scanInputRef])

  const clearIdle = () => {
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current)
      idleTimerRef.current = null
    }
  }

  const scheduleIdle = () => {
    if (!idleMs) return
    clearIdle()
    idleTimerRef.current = setTimeout(processBuffer, idleMs)
  }

  const processBuffer = () => {
    const text = bufferRef.current.trim()
    setBuffer('')
    clearIdle()

    // limpiar sumideros
    if (sinkElRef.current) sinkElRef.current.textContent = ''
    if (inputElRef.current) inputElRef.current.value = ''

    if (!text) return
    const rut = extractRutFromText(text)
    if (!rut) return toast?.error?.('No se encontró RUT en el texto leído')

    const formatted = formatDocumentSync(rut)
    if (!formatted) return toast?.error?.('RUT inválido')

    onInsert(formatted)

    if (autoSubmitRef.current) {
      const isQuery = defaultEventRef.current === 'query'
      if (canAutoSubmitRef.current || isQuery) {
        onSubmit(formatted, defaultEventRef.current)
      } else {
        toast?.warn?.('Selecciona una dependencia antes de auto-enviar')
      }
    }
  }

  // 1) Listener global de teclado (por si el lector manda keydown)
  useEffect(() => {
    if (!enabled) return
    const onKeyDown = (e) => {
      if (!enabledRef.current) return
      if (e.ctrlKey || e.metaKey || e.altKey) return

      // Enter → procesa ya
      if (e.key === 'Enter') {
        e.preventDefault()
        processBuffer()
        return
      }

      // Carácter imprimible → acumula y arma idle
      if (e.key.length === 1) {
        setBuffer(prev => prev + e.key)
        scheduleIdle()
      }
    }

    window.addEventListener('keydown', onKeyDown, true)
    return () => {
      window.removeEventListener('keydown', onKeyDown, true)
      clearIdle()
    }
  }, [enabled, idleMs])

  // 2) Sumidero contentEditable (PC sin Enter + algunas pistolas)
  useEffect(() => {
    if (!enabled) return
    const sink = sinkElRef.current
    if (!sink) return

    const onInput = () => {
      // tomar todo el contenido actual (evita perder chars por frames)
      const text = sink.textContent || ''
      setBuffer(text)
      scheduleIdle()
    }
    const onKeyDown = (e) => {
      if (e.key === 'Enter') { e.preventDefault(); processBuffer() }
    }

    sink.addEventListener('input', onInput)
    sink.addEventListener('keydown', onKeyDown)
    return () => {
      sink.removeEventListener('input', onInput)
      sink.removeEventListener('keydown', onKeyDown)
    }
  }, [enabled, idleMs])

  // 3) Input hidden (móviles / ScanKey)
  useEffect(() => {
    if (!enabled) return
    const el = inputElRef.current
    if (!el) return

    const onInput = () => {
      setBuffer(el.value || '')
      scheduleIdle()
    }
    const onKeyDown = (e) => {
      if (e.key === 'Enter') { e.preventDefault(); processBuffer() }
    }

    el.addEventListener('input', onInput)
    el.addEventListener('keydown', onKeyDown)
    return () => {
      el.removeEventListener('input', onInput)
      el.removeEventListener('keydown', onKeyDown)
    }
  }, [enabled, idleMs])
}
